import qs from "qs"
import { useEffect, useState } from "react"
import {
  LoaderCircleIcon,
  PlusIcon,
  SaveIcon,
  BoldIcon,
  ItalicIcon,
  UnderlineIcon,
  StrikethroughIcon,
  LinkIcon,
  UndoIcon,
  RedoIcon,
  RemoveFormattingIcon,
  XIcon,
} from "lucide-react"
import { useFetcher, useNavigate, useParams, useRouteLoaderData, useOutletContext } from "react-router"
import { useForm, Controller } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { useEditor, EditorContent, type Editor } from "@tiptap/react"
import { useEditorState } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import Underline from "@tiptap/extension-underline"
import Link from "@tiptap/extension-link"
import { fetchAPI, reactHookFormSubmitShim, throwIfNotOk, ApiRequestError } from "@/utils"
import type { RootLoader } from "../root"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { PopoverClose } from "@radix-ui/react-popover"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import {
  GuidanceFormSchema,
  type GuidanceFormData,
  LinkInputSchema,
  type LinkInputData,
  validateFqdnUrl,
} from "@/schemas/reviewer/guidance"
import { type StoredSession } from "@/components/Filters"
import { formatFluentValidationErrors, formatRHFErrors, type FluentValidationError } from "@/utils/forms"
import { ValidationSummary } from "@/components/ui/ValidationSummary"
import type { Guidance } from "./guidance"

const sessionKey = "guidance-filter"

interface ActionResponse {
  success: boolean
  errorMessage?: string
  errors: Record<string, string[]>
}

interface OutletContextType {
  loadedOrganisation?: string
  guidanceItems?: Guidance[]
}

interface DuplicateFieldError {
  field: string
  reason: string
}

interface DuplicateResponse {
  type: string
  fields: DuplicateFieldError[]
}

async function action({ request, params }: { request: Request; params: { name?: string } }): Promise<ActionResponse> {
  const formData = qs.parse(await request.text()) as unknown as GuidanceFormData

  if (formData.organisation == null || formData.organisation === "") {
    return {
      success: false,
      errorMessage: "Missing organisation",
      errors: { organisation: ["Organisation is required"] },
    }
  }

  const validationResult = GuidanceFormSchema.safeParse({
    name: formData.name,
    code: formData.code,
    value: formData.value,
    organisation: formData.organisation,
  })

  if (!validationResult.success) {
    return {
      success: false,
      errorMessage: "Validation failed",
      errors: {},
    }
  }

  const isEditMode = params.name != null && params.name !== ""

  let errors: Record<string, string[]> = {}
  let errorMessage: string | undefined

  try {
    if (isEditMode) {
      const { code, value } = validationResult.data

      await fetchAPI(`/api/UnityReview/Guidance/${formData.organisation}/${params.name}`, {
        method: "PUT",
        body: { code, value },
      }).then(throwIfNotOk)
    } else {
      await fetchAPI(`/api/UnityReview/Guidance/${formData.organisation}`, {
        method: "POST",
        body: validationResult.data,
      }).then(throwIfNotOk)
    }

    return { success: true, errors: {} }
  } catch (error) {
    if (error instanceof ApiRequestError && error.status === 400) {
      if (Array.isArray(error.body)) {
        errors = formatFluentValidationErrors(error.body as FluentValidationError[])
      } else {
        console.error(error)
        errorMessage = "Error saving guidance"
      }
    } else if (error instanceof ApiRequestError && error.status === 409) {
      const body = error.body as DuplicateResponse | null

      if (body?.type === "Duplicate" && Array.isArray(body.fields) && body.fields.length > 0) {
        errors = body.fields.reduce<Record<string, string[]>>((acc, { field, reason }) => {
          const key = field.charAt(0).toLowerCase() + field.slice(1)
          acc[key] = [...(acc[key] ?? []), reason]
          return acc
        }, {})
      } else {
        console.error(error)
        errorMessage = "Error saving guidance"
      }
    } else {
      console.error(error)
      errorMessage = isEditMode ? "Error updating guidance" : "Error creating guidance"
    }
  }

  return {
    success: false,
    errorMessage,
    errors,
  }
}
interface LinkDialogProps {
  onAdd: (url: string) => void
}

function LinkDialog({ onAdd }: LinkDialogProps) {
  const [protocol, setProtocol] = useState<"https://" | "http://">("https://")
  const [urlPath, setUrlPath] = useState("")
  const [error, setError] = useState<string>("")

  const handleAdd = () => {
    setError("")

    const linkData: LinkInputData = { protocol, urlPath }
    const result = LinkInputSchema.safeParse(linkData)

    if (!result.success) {
      const firstError = result.error.issues[0]
      if (firstError != null) {
        setError(firstError.message)
      } else {
        setError("Invalid URL")
      }
      return
    }

    onAdd(result.data)
    setUrlPath("")
    setProtocol("https://")
  }

  return (
    <div className="flex flex-col gap-2 p-3">
      <div className="flex items-center gap-2">
        <Select value={protocol} onValueChange={(value) => setProtocol(value as "https://" | "http://")}>
          <SelectTrigger className="h-8 w-28">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="https://">https://</SelectItem>
            <SelectItem value="http://">http://</SelectItem>
          </SelectContent>
        </Select>
        <Input
          type="text"
          placeholder="example.com"
          value={urlPath}
          onChange={(e) => {
            setUrlPath(e.target.value)
            setError("")
          }}
          className="h-8 flex-1 text-sm"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault()
              handleAdd()
            }
          }}
          autoFocus
        />
      </div>
      {error !== "" && <p className="text-xs text-destructive">{error}</p>}
      <div className="flex items-center justify-end gap-2">
        <PopoverClose asChild>
          <Button type="button" size="sm" variant="ghost" className="h-7">
            Cancel
          </Button>
        </PopoverClose>
        <Button type="button" size="sm" onClick={handleAdd} className="h-7">
          Add Link
        </Button>
      </div>
    </div>
  )
}

function RichTextEditor({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
        blockquote: false,
        codeBlock: false,
        horizontalRule: false,
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: "text-info underline cursor-pointer",
        },
        validate: undefined,
        isAllowedUri: (url) => validateFqdnUrl(url),
      }),
    ],
    content: value,
    onUpdate: ({ editor }: { editor: Editor }) => {
      onChange(editor.getHTML())
    },
    editorProps: {
      attributes: {
        class:
          "prose prose-sm dark:prose-invert max-w-none p-4 border rounded-md min-h-[400px] focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 overflow-y-auto max-h-[600px]",
      },
    },
  })

  const isLinkActive = useEditorState({
    editor,
    selector: (ctx) => ctx.editor.isActive("link"),
  })

  const addLink = (url: string) => {
    if (editor != null) {
      editor.chain().focus().insertContent(`<a href="${url}">${url}</a>`).run()
    }
  }

  const removeLink = () => {
    if (editor != null) {
      editor.chain().focus().unsetLink().run()
    }
  }

  if (editor == null) return null

  return (
    <div className="rounded-md border">
      <div className="flex flex-wrap items-center gap-1 border-b bg-muted p-2">
        <div className="flex items-center gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                size="sm"
                variant={editor.isActive("bold") ? "secondary" : "ghost"}
                onClick={() => editor.chain().focus().toggleBold().run()}
                className="h-8 w-8 p-0"
              >
                <BoldIcon className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Bold</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                size="sm"
                variant={editor.isActive("italic") ? "secondary" : "ghost"}
                onClick={() => editor.chain().focus().toggleItalic().run()}
                className="h-8 w-8 p-0"
              >
                <ItalicIcon className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Italic</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                size="sm"
                variant={editor.isActive("underline") ? "secondary" : "ghost"}
                onClick={() => editor.chain().focus().toggleUnderline().run()}
                className="h-8 w-8 p-0"
              >
                <UnderlineIcon className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Underline</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                size="sm"
                variant={editor.isActive("strike") ? "secondary" : "ghost"}
                onClick={() => editor.chain().focus().toggleStrike().run()}
                className="h-8 w-8 p-0"
              >
                <StrikethroughIcon className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Strikethrough</TooltipContent>
          </Tooltip>
        </div>

        <div className="mx-1 h-6 w-px bg-secondary" />

        <div className="flex items-center gap-1">
          <Popover>
            <Tooltip>
              <PopoverTrigger asChild>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    size="sm"
                    variant={isLinkActive ? "secondary" : "ghost"}
                    className="h-8 w-8 p-0"
                  >
                    <LinkIcon className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
              </PopoverTrigger>
              <TooltipContent>Add Link</TooltipContent>
            </Tooltip>
            <PopoverContent className="w-auto">
              <LinkDialog onAdd={addLink} />
            </PopoverContent>
          </Popover>

          {isLinkActive && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={removeLink}
                  className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                >
                  <XIcon className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Remove Link</TooltipContent>
            </Tooltip>
          )}
        </div>

        <div className="mx-1 h-6 w-px bg-secondary" />

        <div className="flex items-center gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => {
                  if (editor.can().chain().focus().undo().run() === true) {
                    editor.chain().focus().undo().run()
                  }
                }}
                disabled={!editor.can().chain().focus().undo().run()}
                className="h-8 w-8 p-0"
              >
                <UndoIcon className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Undo</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => {
                  if (editor.can().chain().focus().redo().run() === true) {
                    editor.chain().focus().redo().run()
                  }
                }}
                disabled={!editor.can().chain().focus().redo().run()}
                className="h-8 w-8 p-0"
              >
                <RedoIcon className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Redo</TooltipContent>
          </Tooltip>
        </div>

        <div className="mx-1 h-6 w-px bg-secondary" />

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => editor.chain().focus().unsetAllMarks().run()}
              className="h-8 w-8 p-0"
            >
              <RemoveFormattingIcon className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Clear Formatting</TooltipContent>
        </Tooltip>
      </div>

      <EditorContent editor={editor} />
    </div>
  )
}

export function GuidanceForm() {
  const navigate = useNavigate()
  const params = useParams()
  const fetcher = useFetcher<ActionResponse>()
  const rootData = useRouteLoaderData<RootLoader>("root")
  const outletContext = useOutletContext<OutletContextType>()
  const urlSearchParams = new URLSearchParams(window.location.search)
  const page = urlSearchParams.get("page") ?? "1"
  const pageSize = urlSearchParams.get("pageSize") ?? "10"

  const isEditMode = params.name != null && params.name !== ""

  const guidance =
    isEditMode && outletContext.guidanceItems != null
      ? (outletContext.guidanceItems.find((item) => item.name === params.name) ?? null)
      : null

  const filters = qs.parse(sessionStorage.getItem(sessionKey) ?? "") as unknown as Partial<StoredSession>
  const currentOrganisation =
    outletContext?.loadedOrganisation ?? filters.parsed?.organisation ?? rootData?.organisations?.[0] ?? ""

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<GuidanceFormData>({
    resolver: zodResolver(GuidanceFormSchema),
    defaultValues: {
      organisation: currentOrganisation,
      name: guidance?.name ?? "",
      code: guidance?.code ?? "",
      value: guidance?.value ?? "",
    },
  })

  useEffect(() => {
    if (fetcher.data?.success === true) {
      navigate(`/applications/Unity Review/guidance?page=${page}&pageSize=${pageSize}`)
    }
  }, [fetcher.data?.success, navigate, page, pageSize])

  const handleFormSubmit = handleSubmit((formData) => {
    reactHookFormSubmitShim(formData, fetcher.submit)
  })

  const editorKey = isEditMode && guidance != null ? `edit-${guidance.name}` : "create"

  const zodSummary = formatRHFErrors(errors)
  const serverSummary = fetcher.data?.errors ?? {}
  const combinedErrors = { ...zodSummary, ...serverSummary }

  return (
    <div className="h-full overflow-y-auto">
      <fetcher.Form
        method="POST"
        className="space-y-6 pb-6"
        noValidate
        onSubmit={(e) => {
          void handleFormSubmit(e)
        }}
      >
        <input type="hidden" {...register("organisation")} />

        {fetcher.data?.errorMessage != null && Object.keys(fetcher.data.errors).length === 0 && (
          <div className="relative w-full rounded-lg border border-red-200 bg-red-50 p-4 text-red-900">
            <div className="text-sm">{fetcher.data.errorMessage}</div>
          </div>
        )}

        <ValidationSummary errors={combinedErrors} />

        <div>
          <label htmlFor="name" className="mb-2 block text-sm font-medium">
            Name {!isEditMode && <span className="text-red-600">*</span>}
          </label>
          <Input
            id="name"
            {...register("name")}
            disabled={isEditMode}
            className={isEditMode ? "bg-muted" : ""}
            placeholder="Enter unique name"
          />
          {isEditMode && <p className="mt-1 text-xs text-muted-foreground">Name cannot be changed after creation</p>}
        </div>

        <div>
          <label htmlFor="code" className="mb-2 block text-sm font-medium">
            Code <span className="text-red-600">*</span>
          </label>
          <Input id="code" {...register("code")} placeholder="Enter code" />
        </div>

        <div>
          <label htmlFor="value" className="mb-2 block text-sm font-medium">
            Value <span className="text-red-600">*</span>
          </label>
          <Controller
            name="value"
            control={control}
            render={({ field }) => <RichTextEditor key={editorKey} value={field.value} onChange={field.onChange} />}
          />
        </div>

        <div className="flex items-center justify-end gap-4 border-t pt-4">
          {fetcher.state !== "idle" && (
            <span className="flex items-center justify-start gap-1 pr-4">
              <LoaderCircleIcon className="size-4 animate-spin" />
              {isEditMode ? "Updating..." : "Creating..."}
            </span>
          )}
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              navigate(`/applications/Unity Review/guidance?page=${page}&pageSize=${pageSize}`)
            }}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={fetcher.state !== "idle"}>
            {isEditMode ? (
              <>
                <SaveIcon className="mr-2 h-4 w-4" /> Update Guidance
              </>
            ) : (
              <>
                <PlusIcon className="mr-2 h-4 w-4" /> Create Guidance
              </>
            )}
          </Button>
        </div>
      </fetcher.Form>
    </div>
  )
}

GuidanceForm.action = action
